import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  CircleDashed,
  Inbox,
  ClipboardCheck,
  Users,
  Building2,
  Minus,
  Send,
  Filter,
  X,
  Download,
  Upload,
  FileSpreadsheet,
  CalendarClock,
  AlertTriangle,
  Paperclip,
  FileText,
  Image,
  Eye,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Letters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize filters from URL params
  const initialStatusFilter = searchParams.get("status") || "all";
  const initialTransferFilter = searchParams.get("type_of_transfer") || "all";

  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [transferFilter, setTransferFilter] = useState(initialTransferFilter);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingLetter, setEditingLetter] = useState(null);
  const [deletingLetter, setDeletingLetter] = useState(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    folio: "",
    name: "",
    type_of_letter: "Transfer Letter",
    type_of_transfer: "In",
    status: "Not Yet Initiated",
    church: "",
    telephone: "",
    comments: "",
    date_received: "",
    follow_up_date: "",
    attachment: null,
  });
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");

  const STATUS_OPTIONS = [
    "Not Yet Initiated",
    "Just Received",
    "Board Approved",
    "EC Approved",
    "Business Meeting Approved",
    "Sent",
    "Completed",
  ];

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(letters.map((l) => l.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    try {
      const ids = Array.from(selectedIds);
      const res = await axios.post(`${API}/letters/bulk-delete`, { ids });
      toast.success(`Deleted ${res.data.deleted} letters`);
      clearSelection();
      setIsBulkDeleteOpen(false);
      fetchLetters();
    } catch (e) {
      toast.error("Failed to delete selected letters");
    }
  };

  const handleBulkStatus = async (newStatus) => {
    if (!newStatus) return;
    try {
      const ids = Array.from(selectedIds);
      const res = await axios.post(`${API}/letters/bulk-status`, { ids, status: newStatus });
      toast.success(`Updated ${res.data.updated} letters to "${newStatus}"`);
      clearSelection();
      setBulkStatus("");
      fetchLetters();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const fetchLetters = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (transferFilter !== "all") params.append("type_of_transfer", transferFilter);
      if (searchTerm) params.append("search", searchTerm);

      const response = await axios.get(`${API}/letters?${params.toString()}`);
      setLetters(response.data);
    } catch (error) {
      console.error("Error fetching letters:", error);
      toast.error("Failed to fetch letters");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLetters();
  }, [statusFilter, transferFilter, searchTerm]);

  // Auto-open edit dialog when navigated from dashboard with ?edit=<letter_id>
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || letters.length === 0) return;
    const target = letters.find((l) => l.id === editId);
    if (target) {
      handleOpenDialog(target);
      // Clear ?edit so refresh doesn't re-open
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [letters, searchParams]);

  const handleOpenDialog = async (letter = null) => {
    if (letter) {
      setEditingLetter(letter);
      setFormData({
        folio: letter.folio,
        name: letter.name,
        type_of_letter: letter.type_of_letter,
        type_of_transfer: letter.type_of_transfer,
        status: letter.status,
        church: letter.church,
        telephone: letter.telephone || "",
        comments: letter.comments || "",
        date_received: letter.date_received || "",
        follow_up_date: letter.follow_up_date || "",
        attachment: letter.attachment || null,
      });
      setAttachmentFile(null);
    } else {
      setEditingLetter(null);
      // Get next folio number for new letters and set today's date as default
      const today = new Date().toISOString().split('T')[0];
      try {
        const response = await axios.get(`${API}/letters/next-folio`);
        setFormData({
          folio: response.data.folio,
          name: "",
          type_of_letter: "Transfer Letter",
          type_of_transfer: "In",
          status: "Not Yet Initiated",
          church: "",
          telephone: "",
          comments: "",
          date_received: today,
          follow_up_date: "",
          attachment: null,
        });
      } catch (error) {
        setFormData({
          folio: "",
          name: "",
          type_of_letter: "Transfer Letter",
          type_of_transfer: "In",
          status: "Not Yet Initiated",
          church: "",
          telephone: "",
          comments: "",
          date_received: today,
          follow_up_date: "",
          attachment: null,
        });
      }
      setAttachmentFile(null);
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.church) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      let attachmentData = formData.attachment;
      
      // Upload new file if selected
      if (attachmentFile) {
        setUploading(true);
        const uploadForm = new FormData();
        uploadForm.append('file', attachmentFile);
        
        try {
          const uploadRes = await axios.post(`${API}/upload`, uploadForm);
          attachmentData = uploadRes.data;
        } catch (uploadError) {
          toast.error("Failed to upload attachment");
          setUploading(false);
          return;
        }
        setUploading(false);
      }
      
      const submitData = { ...formData, attachment: attachmentData };
      
      if (editingLetter) {
        await axios.put(`${API}/letters/${editingLetter.id}`, submitData);
        toast.success("Letter updated successfully");
      } else {
        await axios.post(`${API}/letters`, submitData);
        toast.success("Letter created successfully");
      }
      setIsDialogOpen(false);
      setAttachmentFile(null);
      fetchLetters();
    } catch (error) {
      console.error("Error saving letter:", error);
      toast.error("Failed to save letter");
    }
  };

  const handleDelete = async () => {
    if (!deletingLetter) return;
    
    try {
      await axios.delete(`${API}/letters/${deletingLetter.id}`);
      toast.success("Letter deleted successfully");
      setIsDeleteDialogOpen(false);
      setDeletingLetter(null);
      fetchLetters();
    } catch (error) {
      console.error("Error deleting letter:", error);
      toast.error("Failed to delete letter");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTransferFilter("all");
    setSearchParams({});
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/letters/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'letters_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/letters/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'letters_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to export Excel");
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const response = await axios.post(`${API}/letters/import`, formData);
      const { imported = 0, updated = 0, skipped = 0 } = response.data;
      toast.success(`Imported ${imported} new, updated ${updated} existing letters`);
      if (skipped > 0) {
        toast.info(`Skipped ${skipped} empty rows`);
      }
      setIsImportDialogOpen(false);
      setImportFile(null);
      fetchLetters();
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import file");
    } finally {
      setImporting(false);
    }
  };

  const openAttachment = async (attachment) => {
    if (!attachment?.storage_path) {
      toast.error("Attachment unavailable");
      return;
    }
    try {
      const res = await axios.get(`${API}/files`, {
        params: { path: attachment.storage_path },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: attachment.content_type || 'application/octet-stream' }));
      window.open(blobUrl, '_blank');
      // Revoke after a delay so the new tab can load it
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error("Failed to open attachment");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="letters-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="letters-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
            Letters
          </h1>
          <p className="text-slate-500 mt-2 font-body">
            Manage all your church transfer letters
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setIsImportDialogOpen(true)}
            className="rounded-full"
            data-testid="import-btn"
          >
            <Upload size={18} className="mr-2" />
            Import
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            className="rounded-full"
            data-testid="export-csv-btn"
          >
            <Download size={18} className="mr-2" />
            CSV
          </Button>
          <Button
            variant="outline"
            onClick={handleExportExcel}
            className="rounded-full"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet size={18} className="mr-2" />
            Excel
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-blue-900 hover:bg-blue-800 text-white rounded-full px-6"
            data-testid="add-letter-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Letter
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6 shadow-sm border border-slate-100" data-testid="filters-card">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search by name, folio, or church..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 h-11" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Not Yet Initiated">Not Yet Initiated</SelectItem>
                <SelectItem value="Just Received">Just Received</SelectItem>
                <SelectItem value="Board Approved">Board Approved</SelectItem>
                <SelectItem value="EC Approved">EC Approved</SelectItem>
                <SelectItem value="Business Meeting Approved">Business Meeting Approved</SelectItem>
                <SelectItem value="Sent">Sent</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={transferFilter} onValueChange={setTransferFilter}>
              <SelectTrigger className="w-full md:w-48 h-11" data-testid="transfer-filter">
                <SelectValue placeholder="Transfer Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Transfers</SelectItem>
                <SelectItem value="In">Transfer In</SelectItem>
                <SelectItem value="Out">Transfer Out</SelectItem>
                <SelectItem value="N/A">N/A</SelectItem>
              </SelectContent>
            </Select>
            {(searchTerm || statusFilter !== "all" || transferFilter !== "all") && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="h-11"
                data-testid="clear-filters-btn"
              >
                <X size={18} className="mr-2" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bulk action toolbar */}
      {selectedIds.size > 0 && (
        <Card className="mb-4 shadow-sm border border-blue-200 bg-blue-50" data-testid="bulk-toolbar">
          <CardContent className="py-3 px-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-blue-900 font-body">
                {selectedIds.size} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="text-blue-700 hover:bg-blue-100 h-8"
                data-testid="clear-selection-btn"
              >
                Clear
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={bulkStatus} onValueChange={(v) => { setBulkStatus(v); handleBulkStatus(v); }}>
                <SelectTrigger className="w-56 h-9 bg-white" data-testid="bulk-status-select">
                  <SelectValue placeholder="Change status to..." />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => setIsBulkDeleteOpen(true)}
                variant="outline"
                className="rounded-full text-red-600 border-red-200 hover:bg-red-50 h-9"
                data-testid="bulk-delete-btn"
              >
                <Trash2 size={16} className="mr-2" /> Delete selected
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Letters Table */}
      <Card className="shadow-sm border border-slate-100" data-testid="letters-table-card">
        <CardContent className="p-0">
          {letters.length === 0 ? (
            <div className="empty-state py-16" data-testid="empty-letters">
              <img
                src="https://images.unsplash.com/photo-1642437832852-4f6192df4548?crop=entropy&cs=srgb&fm=jpg&q=85&w=300"
                alt="No letters"
                className="mx-auto mb-4"
              />
              <p className="text-slate-500 font-body text-lg mb-2">No letters found</p>
              <p className="text-slate-400 font-body text-sm">
                {searchTerm || statusFilter !== "all" || transferFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Click 'Add Letter' to create your first letter"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="letters-table">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-4 px-3 w-10">
                      <Checkbox
                        checked={letters.length > 0 && selectedIds.size === letters.length}
                        onCheckedChange={(v) => toggleSelectAll(!!v)}
                        data-testid="select-all-checkbox"
                        aria-label="Select all"
                      />
                    </th>
                    <th className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-slate-500 font-body">Folio</th>
                    <th className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-slate-500 font-body">Name</th>
                    <th className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-slate-500 font-body">Transfer</th>
                    <th className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-slate-500 font-body">Status</th>
                    <th className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-slate-500 font-body">Received</th>
                    <th className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-slate-500 font-body">Follow-up</th>
                    <th className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-slate-500 font-body">Attachment</th>
                    <th className="text-right py-4 px-4 text-xs uppercase tracking-wider font-bold text-slate-500 font-body">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {letters.map((letter) => {
                    const today = new Date().toISOString().split('T')[0];
                    const isOverdue = letter.follow_up_date && letter.follow_up_date < today && letter.status !== "Completed";
                    return (
                    <tr
                      key={letter.id}
                      className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${isOverdue ? 'bg-red-50' : ''} ${selectedIds.has(letter.id) ? 'bg-blue-50' : ''}`}
                      data-testid={`letter-row-${letter.id}`}
                    >
                      <td className="py-4 px-3 w-10">
                        <Checkbox
                          checked={selectedIds.has(letter.id)}
                          onCheckedChange={() => toggleSelect(letter.id)}
                          data-testid={`select-checkbox-${letter.id}`}
                          aria-label={`Select ${letter.folio}`}
                        />
                      </td>
                      <td className="py-4 px-4 text-sm font-semibold text-slate-900 font-body">
                        {isOverdue && <AlertTriangle size={14} className="inline mr-1 text-red-500" />}
                        {letter.folio}
                      </td>
                      <td className="py-4 px-4 text-sm text-slate-700 font-body">{letter.name}</td>
                      <td className="py-4 px-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium font-body ${
                            letter.type_of_transfer === "In"
                              ? "bg-emerald-50 text-emerald-700"
                              : letter.type_of_transfer === "Out"
                              ? "bg-amber-50 text-amber-700"
                              : "bg-slate-100 text-slate-600"
                          }`}
                          data-testid={`transfer-badge-${letter.id}`}
                        >
                          {letter.type_of_transfer === "In" ? (
                            <ArrowDownLeft size={12} />
                          ) : letter.type_of_transfer === "Out" ? (
                            <ArrowUpRight size={12} />
                          ) : (
                            <Minus size={12} />
                          )}
                          {letter.type_of_transfer}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium font-body border ${
                            letter.status === "Completed"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : letter.status === "Just Received"
                              ? "bg-sky-50 text-sky-700 border-sky-200"
                              : letter.status === "Board Approved"
                              ? "bg-violet-50 text-violet-700 border-violet-200"
                              : letter.status === "EC Approved"
                              ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                              : letter.status === "Business Meeting Approved"
                              ? "bg-amber-50 text-amber-700 border-amber-200"
                              : letter.status === "Sent"
                              ? "bg-cyan-50 text-cyan-700 border-cyan-200"
                              : "bg-slate-100 text-slate-600 border-slate-200"
                          }`}
                          data-testid={`status-badge-${letter.id}`}
                        >
                          {letter.status === "Completed" ? (
                            <CheckCircle2 size={12} />
                          ) : letter.status === "Just Received" ? (
                            <Inbox size={12} />
                          ) : letter.status === "Board Approved" ? (
                            <ClipboardCheck size={12} />
                          ) : letter.status === "EC Approved" ? (
                            <Users size={12} />
                          ) : letter.status === "Business Meeting Approved" ? (
                            <Building2 size={12} />
                          ) : letter.status === "Sent" ? (
                            <Send size={12} />
                          ) : (
                            <CircleDashed size={12} />
                          )}
                          {letter.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-slate-500 font-body">{letter.date_received || "-"}</td>
                      <td className="py-4 px-4">
                        {letter.follow_up_date ? (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium font-body ${isOverdue ? 'text-red-600' : 'text-slate-500'}`}>
                            <CalendarClock size={12} />
                            {letter.follow_up_date}
                            {isOverdue && <span className="text-red-600 font-semibold ml-1">OVERDUE</span>}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        {letter.attachment ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openAttachment(letter.attachment)}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            title={letter.attachment.original_filename}
                            data-testid={`attachment-btn-${letter.id}`}
                          >
                            {letter.attachment.content_type?.startsWith('image/') ? (
                              <Image size={16} />
                            ) : (
                              <FileText size={16} />
                            )}
                          </Button>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(letter)}
                            className="text-slate-600 hover:text-blue-900 hover:bg-blue-50"
                            data-testid={`edit-btn-${letter.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeletingLetter(letter);
                              setIsDeleteDialogOpen(true);
                            }}
                            className="text-slate-600 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-btn-${letter.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto bg-white rounded-xl p-0" data-testid="letter-dialog">
          <div className="sticky top-0 bg-white z-10 px-6 pt-6 pb-4 border-b border-slate-100">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-semibold text-slate-900">
                {editingLetter ? "Edit Letter" : "Add New Letter"}
              </DialogTitle>
              <p className="text-sm text-slate-500 font-body">
                {editingLetter ? "Update the letter details below" : "Fill in the letter details to create a new record"}
              </p>
            </DialogHeader>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-medium text-slate-700 font-body">
                Folio <span className="text-slate-400 text-xs">(Auto-generated)</span>
              </Label>
              <Input
                value={formData.folio}
                onChange={(e) => setFormData({ ...formData, folio: e.target.value })}
                placeholder="Auto-generated"
                className="mt-2 h-12 bg-slate-50"
                disabled={!editingLetter}
                data-testid="input-folio"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 font-body">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter name"
                className="mt-2 h-12"
                data-testid="input-name"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 font-body">Type of Letter</Label>
              <Select
                value={formData.type_of_letter}
                onValueChange={(val) => setFormData({ ...formData, type_of_letter: val })}
              >
                <SelectTrigger className="mt-2 h-12" data-testid="select-type-letter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Transfer Letter">Transfer Letter</SelectItem>
                  <SelectItem value="Confirmation">Confirmation</SelectItem>
                  <SelectItem value="Service Request">Service Request</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 font-body">Type of Transfer</Label>
              <Select
                value={formData.type_of_transfer}
                onValueChange={(val) => setFormData({ ...formData, type_of_transfer: val })}
              >
                <SelectTrigger className="mt-2 h-12" data-testid="select-type-transfer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="In">In</SelectItem>
                  <SelectItem value="Out">Out</SelectItem>
                  <SelectItem value="N/A">N/A</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 font-body">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(val) => setFormData({ ...formData, status: val })}
              >
                <SelectTrigger className="mt-2 h-12" data-testid="select-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Not Yet Initiated">Not Yet Initiated</SelectItem>
                  <SelectItem value="Just Received">Just Received</SelectItem>
                  <SelectItem value="Board Approved">Board Approved</SelectItem>
                  <SelectItem value="EC Approved">EC Approved</SelectItem>
                  <SelectItem value="Business Meeting Approved">Business Meeting Approved</SelectItem>
                  <SelectItem value="Sent">Sent</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 font-body">
                Church <span className="text-red-500">*</span>
              </Label>
              <Input
                value={formData.church}
                onChange={(e) => setFormData({ ...formData, church: e.target.value })}
                placeholder="Enter church name"
                className="mt-2 h-12"
                data-testid="input-church"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 font-body">
                Telephone <span className="text-slate-400 text-xs">(Max 10 digits)</span>
              </Label>
              <Input
                value={formData.telephone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({ ...formData, telephone: value });
                }}
                placeholder="Enter phone number"
                className="mt-2 h-12"
                maxLength={10}
                inputMode="numeric"
                pattern="[0-9]*"
                data-testid="input-telephone"
              />
              {formData.telephone && formData.telephone.length > 0 && (
                <p className="text-xs text-slate-400 mt-1 font-body">{formData.telephone.length}/10 digits</p>
              )}
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 font-body">
                Date Received <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                value={formData.date_received}
                onChange={(e) => setFormData({ ...formData, date_received: e.target.value })}
                className="mt-2 h-12"
                data-testid="input-date-received"
              />
              <p className="text-xs text-slate-400 mt-1 font-body">Used for monthly transfer activity tracking</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 font-body">
                Follow-up Date
              </Label>
              <Input
                type="date"
                value={formData.follow_up_date}
                onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
                className="mt-2 h-12"
                data-testid="input-follow-up-date"
              />
              <p className="text-xs text-slate-400 mt-1 font-body">When action/follow-up is needed</p>
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium text-slate-700 font-body">Comments</Label>
              <Textarea
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                placeholder="Enter any additional comments"
                className="mt-2"
                rows={3}
                data-testid="input-comments"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium text-slate-700 font-body">
                Attachment <span className="text-slate-400 text-xs">(Image, PDF, Word, Excel)</span>
              </Label>
              <div className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-4">
                {formData.attachment && !attachmentFile ? (
                  <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      {formData.attachment.content_type?.startsWith('image/') ? (
                        <Image size={20} className="text-blue-600" />
                      ) : (
                        <FileText size={20} className="text-blue-600" />
                      )}
                      <span className="text-sm font-medium text-slate-700">{formData.attachment.original_filename}</span>
                      <span className="text-xs text-slate-400">({Math.round(formData.attachment.size / 1024)} KB)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openAttachment(formData.attachment)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Eye size={16} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData({ ...formData, attachment: null })}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  </div>
                ) : attachmentFile ? (
                  <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Paperclip size={20} className="text-blue-600" />
                      <span className="text-sm font-medium text-blue-700">{attachmentFile.name}</span>
                      <span className="text-xs text-blue-500">({Math.round(attachmentFile.size / 1024)} KB)</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setAttachmentFile(null)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center cursor-pointer py-4">
                    <Paperclip size={24} className="text-slate-400 mb-2" />
                    <span className="text-sm text-blue-600 font-medium">Click to upload file</span>
                    <span className="text-xs text-slate-400 mt-1">Max 10MB • Images, PDF, Word, Excel</span>
                    <input
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                      className="hidden"
                      data-testid="input-attachment"
                    />
                  </label>
                )}
              </div>
            </div>
            </div>
          </div>
          <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-slate-100">
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="rounded-full"
                data-testid="cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={uploading}
                className="bg-blue-900 hover:bg-blue-800 text-white rounded-full px-8"
                data-testid="submit-btn"
              >
                {uploading ? "Uploading..." : editingLetter ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">
              Delete Letter
            </AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Are you sure you want to delete this letter for "{deletingLetter?.name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              data-testid="delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">Delete {selectedIds.size} letters?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              This will permanently delete the {selectedIds.size} selected letter{selectedIds.size === 1 ? "" : "s"}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="bulk-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              data-testid="bulk-delete-confirm-btn"
            >
              Delete {selectedIds.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-md w-[95vw] max-h-[90vh] overflow-y-auto bg-white rounded-xl" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-semibold text-slate-900">
              Import Letters
            </DialogTitle>
            <p className="text-sm text-slate-500 font-body">
              Upload a CSV or Excel file. Existing letters with the same folio will be updated; new folios will be added.
            </p>
          </DialogHeader>
          <div className="py-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <FileSpreadsheet className="mx-auto h-12 w-12 text-slate-400 mb-4" />
              <Label
                htmlFor="file-upload"
                className="cursor-pointer text-blue-900 hover:text-blue-800 font-medium font-body"
              >
                Click to select file
              </Label>
              <input
                id="file-upload"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="file-input"
              />
              {importFile && (
                <p className="mt-3 text-sm text-slate-600 font-body">
                  Selected: {importFile.name}
                </p>
              )}
            </div>
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 font-body font-medium mb-2">Expected columns:</p>
              <p className="text-xs text-slate-400 font-body">
                Folio, Name, Type of Letter, Type of Transfer, Status, Church, Telephone, Comments
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsImportDialogOpen(false);
                setImportFile(null);
              }}
              className="rounded-full"
              data-testid="import-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={!importFile || importing}
              className="bg-blue-900 hover:bg-blue-800 text-white rounded-full px-8"
              data-testid="import-submit-btn"
            >
              {importing ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Letters;
